import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Upload, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function UploadAthleticTests() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error('Please select a valid CSV file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/children/${childId}/athletic-tests/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setResult(response.data);
      toast.success(`Successfully uploaded ${response.data.uploaded_count} test results!`);
      
      // Navigate back after 2 seconds
      setTimeout(() => {
        navigate(`/child/${childId}`);
      }, 2000);
    } catch (error) {
      toast.error('Failed to upload athletic tests');
      console.error(error);
      setResult({ error: error.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = `test_date,test_name,result_value
2024-02-15,5-0-5,2.45
2024-02-15,Countermovement Jump,35
2024-02-15,T-Test,10.2
2024-02-15,Standing Sprint,2.15`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'athletic_tests_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#050505] grid-texture">
      <div className="border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate(`/child/${childId}`)}
            className="text-zinc-400 hover:text-white mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Profile
          </Button>
          <h1 className="text-4xl font-bold text-white uppercase tracking-tight" data-testid="upload-tests-title">
            Upload Athletic Tests
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-8">
          {/* Instructions */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-white uppercase tracking-tight mb-4">CSV File Format</h3>
            <div className="bg-[#02b5ff]/10 border border-[#02b5ff]/20 p-4 mb-4">
              <p className="text-sm text-[#02b5ff] mb-2">Supports multiple CSV formats:</p>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-[#02b5ff] font-bold uppercase mb-1">Standard Format:</p>
                  <ul className="text-sm text-[#02b5ff] font-mono space-y-1 ml-4">
                    <li>• <strong>test_date</strong> - Date of test (YYYY-MM-DD format)</li>
                    <li>• <strong>test_name</strong> - Name of the test</li>
                    <li>• <strong>result_value</strong> - Test result (numeric value)</li>
                  </ul>
                </div>
                <div>
                  <p className="text-xs text-[#02b5ff] font-bold uppercase mb-1">Athletic Device Exports (Auto-detected):</p>
                  <ul className="text-sm text-[#02b5ff] font-mono space-y-1 ml-4">
                    <li>• <strong>CMJ</strong> - Date, Jump height, Max/Avg power</li>
                    <li>• <strong>Single Leg Jump</strong> - Date, Leg, Jump height, power</li>
                    <li>• <strong>Standing Sprint</strong> - Date, Total time, Split times, Max speed</li>
                    <li>• <strong>5-0-5</strong> - Date, Direction, 5-0-5 time, accel/decel</li>
                    <li>• <strong>Flying Sprint</strong> - Date, Max speed, Total time</li>
                    <li>• <strong>ADA</strong> - Date, Distance-to-stop, Time-to-stop</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/50 border border-zinc-800 p-4 mb-4">
              <h4 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Supported Test Types:</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-zinc-400 font-mono">
                <div>• Countermovement Jump</div>
                <div>• Single Leg Jump</div>
                <div>• Standing Sprint 20m</div>
                <div>• Standing Sprint 30m</div>
                <div>• 5-0-5 Agility</div>
                <div>• Flying Sprint</div>
                <div>• ADA Test</div>
                <div>• CMJ Max Power</div>
                <div>• CMJ Average Power</div>
                <div>• T-Test</div>
                <div>• Squat Jump</div>
                <div>• Pro Agility</div>
              </div>
            </div>

            <Button
              data-testid="download-template-button"
              onClick={downloadTemplate}
              variant="outline"
              className="h-10 px-6 bg-transparent border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-white font-bold uppercase tracking-wider text-xs"
            >
              <FileText className="mr-2 h-4 w-4" />
              Download Template
            </Button>
          </div>

          {/* Upload Section */}
          <div className="border-t border-zinc-800 pt-8">
            <h3 className="text-xl font-bold text-white uppercase tracking-tight mb-4">Upload File</h3>
            
            <div className="mb-6">
              <input
                data-testid="file-input"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="block w-full text-sm text-zinc-400 font-mono
                  file:mr-4 file:py-3 file:px-6
                  file:border-0
                  file:text-sm file:font-bold file:uppercase file:tracking-wider
                  file:bg-zinc-900 file:text-white
                  hover:file:bg-zinc-800
                  file:cursor-pointer cursor-pointer"
              />
            </div>

            {file && (
              <div className="bg-zinc-900/50 border border-zinc-800 p-4 mb-6">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-[#02b5ff]" />
                  <div>
                    <p className="text-white font-mono text-sm">{file.name}</p>
                    <p className="text-zinc-500 text-xs">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                </div>
              </div>
            )}

            <Button
              data-testid="upload-button"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="h-12 px-8 bg-[#02b5ff] hover:bg-[#02b5ff] text-white font-bold uppercase tracking-wider text-sm w-full hover:shadow-[0_0_30px_rgba(37,99,235,0.4)] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="mr-2 h-5 w-5" />
              {uploading ? 'Uploading...' : 'Upload Test Results'}
            </Button>
          </div>

          {/* Result Display */}
          {result && (
            <div className="border-t border-zinc-800 pt-8 mt-8" data-testid="upload-result">
              {result.error ? (
                <div className="bg-[#ff025f]/10 border border-[#ff025f]/20 p-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-[#ff025f] mt-0.5" />
                    <div>
                      <h4 className="text-[#ff025f] font-bold mb-2">Upload Error</h4>
                      <p className="text-[#ff025f] text-sm">{result.error}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-[#02b5ff]/10 border border-[#02b5ff]/20 p-6">
                    <h4 className="text-[#02b5ff] font-bold uppercase tracking-wider mb-2">Upload Successful!</h4>
                    <p className="text-[#02b5ff] text-sm mb-4">{result.message}</p>
                    <div className="grid grid-cols-2 gap-4 font-mono text-sm">
                      <div>
                        <span className="text-zinc-500">Uploaded:</span>
                        <span className="text-white ml-2 font-bold">{result.uploaded_count}</span>
                      </div>
                      {result.error_count > 0 && (
                        <div>
                          <span className="text-zinc-500">Errors:</span>
                          <span className="text-[#02b5ff] ml-2 font-bold">{result.error_count}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {result.errors && result.errors.length > 0 && (
                    <div className="bg-[#02b5ff]/10 border border-yellow-500/20 p-4">
                      <h4 className="text-[#02b5ff] font-bold text-sm mb-2">Processing Warnings:</h4>
                      <ul className="text-[#02b5ff] text-xs font-mono space-y-1">
                        {result.errors.map((error, idx) => (
                          <li key={idx}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}